// Port of prototype/components/hud/ResourceCounter.jsx (design D3), extended for the sandbox
// screen's counter stack (`client-sandbox-screen` task 5.1/5.1a, design §3.1, visual spec §2).
// UI.md §4: display-only stat pill — no interactive elements, so no hit-target concern.
//
// Three things here are requirements rather than styling, and each is load-bearing enough to
// be worth the extra prop:
//
//   * **The pill is a fixed 136 × 34 box, sized for five digits** (visual spec §2.1). It is not
//     `fit-content`. During playback these numbers change every tick, and a pill that grows as
//     its value crosses a digit boundary makes the whole right-hand stack twitch — the counter
//     would be drawing attention to its own arithmetic instead of to the colony. Because the
//     stack is right-aligned, a fixed box also keeps the three pills' left edges flush.
//   * **A value with no source at the playhead renders {@link COUNTER_PLACEHOLDER}, never the
//     value it was last showing.** The screen is read as diagnosis, and a stale number is
//     indistinguishable on screen from a live one. `null` and a non-finite number both mean
//     "no source": `NaN` printed at the player is a fabricated value in exactly the sense the
//     spec forbids. The label dims with the value so the whole pill reads as unavailable.
//   * **Whole numbers, thousands-separated.** The metric is a population and the nectar pool is
//     a float; neither wants decimals, and both want the digits grouped and tabular so a
//     five-digit reading is legible at 16 px in a corner of a phone.
//
// The **delta** (task 5.1a, artifacts review **B4**) is the score pill's answer to "did my
// change help", derived by `ui/rounds/score-delta.ts`. It renders *beside* the pill rather than
// inside it, deliberately: the pill's fixed width is the point of the pill, and a variable-width
// badge inside it would either shrink the number or reintroduce the twitch. Passing the model's
// `ScoreComparison` in whole — rather than a pre-formatted string — is what makes the first-run
// rule structural: this component renders nothing for `first-run`, so no caller can turn a
// missing predecessor into a comparison against zero.
//
// Live mode's colony HUD (`ui/screens/Colony.tsx`) uses the same component; every addition here
// is optional and additive, and the placeholder/rounding rules are improvements it inherits.

import type { CSSProperties, ReactNode } from 'react';
import type { ScoreComparison } from '../../rounds/score-delta';

/** The pill's fixed width, sized for five digits (visual spec §2.1). */
export const COUNTER_PILL_WIDTH = 136;

/** The pill's fixed height (visual spec §2.1). */
export const COUNTER_PILL_HEIGHT = 34;

/**
 * What a counter shows when the playhead has no source for it (visual spec §2.4). An em dash,
 * not a `0` and not an empty pill: zero bees is a real, terrible answer a colony can have, and
 * an empty pill reads as a rendering bug rather than as missing evidence.
 */
export const COUNTER_PLACEHOLDER = '—';

export interface ResourceCounterProps {
  /** Sprite path (string → 18px pixelated img) or a node (e.g. the score's ★ glyph). */
  icon?: string | ReactNode;
  /**
   * The reading, or `null` where the playhead has no source for it. A non-finite number is
   * treated as `null` — see the header: an absence is never printed as a number.
   */
  value: number | string | null;
  /** Short unit label, e.g. 'bees'. */
  label?: string;
  /**
   * The value's ceiling, rendered as `value/capacity` (WAG-100). The stored-nectar counter's
   * ceiling is the colony's pool capacity — `live cells × per-cell capacity` (SIM-RULES §6) — and
   * without it "1,283" is a number with nothing to measure it against: a hive at a tenth of its
   * capacity and one overflowing read identically.
   *
   * Rendered inside the pill rather than as a second pill, and **in the label column's place**,
   * which is what keeps {@link COUNTER_PILL_WIDTH} untouched: a fraction of two five-digit numbers
   * does not fit beside a word as well, and widening this one pill would ragged the stack's left
   * edge that the fixed width exists to keep flush. The unit is carried by the icon and by the
   * group's own `aria-label` on the calling screen, so nothing is lost but the repetition.
   *
   * Absent, `null` or non-finite means "no ceiling known", and the pill then reads exactly as it
   * always has. An absent *value* suppresses the capacity too: a ceiling beside a placeholder
   * would be half a live reading.
   */
  capacity?: number | null;
  /**
   * The score's comparison against the previous run. Omit it on every pill that is not the
   * score; pass `first-run` and nothing is rendered, which is the spec's requirement rather
   * than a caller's discretion.
   */
  delta?: ScoreComparison | null;
  /** 'dark' floats over the game canvas; 'light' for menus. */
  tone?: 'dark' | 'light';
  style?: CSSProperties;
}

/** The delta badge's ink, by direction. `level` stays neutral: nothing happened, and colouring
 * "nothing happened" as either a success or a failure would overstate it. */
const DELTA_TONE: Record<'up' | 'down' | 'level', { fg: string; bg: string }> = {
  up: { fg: 'var(--success)', bg: 'var(--success-soft)' },
  down: { fg: 'var(--danger)', bg: 'var(--danger-soft)' },
  level: { fg: 'var(--ink-3)', bg: 'var(--paper-1)' },
};

/**
 * The counter's text: whole, grouped digits, or the placeholder.
 *
 * `en-US` is named explicitly rather than relying on the ambient locale, so the separator a
 * test asserts is the separator a player sees — the design system's copy is en-US throughout.
 * A string `value` is passed through untouched: a caller that has already composed its own text
 * (a range, a unit) is not asking for arithmetic.
 */
export function formatCounterValue(value: number | string | null): string {
  if (value === null) {
    return COUNTER_PLACEHOLDER;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (!Number.isFinite(value)) {
    return COUNTER_PLACEHOLDER;
  }
  return Math.round(value).toLocaleString('en-US');
}

export function ResourceCounter({
  icon,
  value,
  label,
  capacity,
  delta,
  tone = 'dark',
  style,
}: ResourceCounterProps) {
  const dark = tone === 'dark';
  const text = formatCounterValue(value);
  const unavailable = text === COUNTER_PLACEHOLDER;
  // A ceiling is only a reading when there is something to read against it — see `capacity`.
  const ceiling =
    !unavailable && typeof capacity === 'number' && Number.isFinite(capacity)
      ? formatCounterValue(capacity)
      : null;
  // Dimmed together, so the pill reads as one unavailable thing rather than as a live label
  // above a broken number (visual spec §2.4, `.ctr.empty`).
  const valueColor = unavailable
    ? dark
      ? 'var(--hud-text-dim)'
      : 'var(--text-muted)'
    : dark
      ? 'var(--hud-text)'
      : 'var(--ink-1)';
  const labelColor = dark ? 'var(--hud-text-dim)' : 'var(--text-muted)';

  return (
    <div
      data-testid="resource-counter"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 6,
        ...style,
      }}
    >
      {delta && delta.kind === 'delta' && (
        <span
          data-testid="resource-counter-delta"
          data-direction={delta.direction}
          // The sentence, not the sign, is what a screen reader should hear: "+52" alone is
          // ambiguous about what moved and against what.
          aria-label={delta.description}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            flex: 'none',
            height: 20,
            padding: '0 7px',
            borderRadius: 'var(--r-pill)',
            background: DELTA_TONE[delta.direction].bg,
            border: `1px solid ${DELTA_TONE[delta.direction].fg}`,
            color: DELTA_TONE[delta.direction].fg,
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 11,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
          }}
        >
          {delta.label}
        </span>
      )}
      <div
        data-testid="resource-counter-pill"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          flex: 'none',
          // Fixed, not fit-content — see the header. Five digits at `--text-hud` fit inside this
          // box with the icon and label, so no reading the sim can produce resizes it.
          width: COUNTER_PILL_WIDTH,
          height: COUNTER_PILL_HEIGHT,
          // **`border-box`, not the browser default.** This app ships no global box-sizing reset
          // (there are six token/app CSS files and none sets one), so `content-box` would put the
          // 18px of horizontal padding and the 3px of border *outside* the declared 136 — the pill
          // measured 156 wide in a browser, against the kit's 136. The fixed width is the whole
          // requirement here (a pill that resizes as its value crosses a digit boundary makes the
          // stack twitch), so the declared number has to be the rendered one.
          boxSizing: 'border-box',
          padding: '0 10px 0 8px',
          borderRadius: 'var(--r-pill)',
          background: dark ? 'var(--hud-bg)' : 'var(--surface-card)',
          border: `1.5px solid ${dark ? 'var(--hud-line)' : 'var(--line)'}`,
          boxShadow: dark ? 'var(--shadow-hud)' : 'var(--shadow-card)',
        }}
      >
        {typeof icon === 'string' ? (
          <img
            src={icon}
            alt=""
            style={{ width: 18, height: 18, flex: 'none', imageRendering: 'pixelated' }}
          />
        ) : (
          icon
        )}
        <span
          style={{
            // Pushed against the label so the digits grow leftwards into the pill's slack
            // instead of shifting the label about (visual spec §2.1).
            marginLeft: 'auto',
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 'var(--text-hud)',
            fontVariantNumeric: 'tabular-nums',
            color: valueColor,
          }}
        >
          {text}
          {ceiling !== null && (
            // Inside the value span, with no whitespace between them, so the two numbers are one
            // string on screen and one string to a screen reader — "1,283/1,530", never two
            // unrelated readings sitting side by side. Smaller and dimmed because the stores are
            // what changes tick to tick; the ceiling is the thing they are read against.
            <span
              data-testid="resource-counter-capacity"
              style={{ fontSize: 11, color: labelColor }}
            >
              /{ceiling}
            </span>
          )}
        </span>
        {label && (
          <span
            style={{
              flex: 'none',
              width: 42,
              textAlign: 'right',
              fontFamily: 'var(--font-body)',
              fontSize: 10,
              fontWeight: 700,
              color: labelColor,
            }}
          >
            {label}
          </span>
        )}
      </div>
    </div>
  );
}
